/**
 * Shared Portal formatting utilities.
 *
 * Consolidates helper functions previously duplicated across
 * shell, profile, calendar, dashboard, people, global-search,
 * and org-tree components.
 */
import { t } from '../../../../common/i18n.js'
import type { Language } from '../../../../common/types/i18n.js'
import { formatDisplayDate } from '../../../../common/utils/format-utils.js'

/** Build initials from separate first/last name strings. */
export const getInitials = (firstName: string, lastName: string): string =>
    `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase()

/** Split a separator-joined string into a trimmed, empties-removed list. */
export const splitList = (value: string | null, separator: string): string[] =>
    value
        ? value
              .split(separator)
              .map((s) => s.trim())
              .filter(Boolean)
        : []

/** Build initials from a single full-name string. */
export const getInitialsFromName = (name: string): string => {
    const parts = name.split(' ')
    return parts
        .slice(0, 2)
        .map((p) => p.charAt(0))
        .join('')
        .toUpperCase()
}

/** Display name: always the full first + last name. */
export const getDisplayName = (emp: { first_name: string; last_name: string }): string =>
    `${emp.first_name} ${emp.last_name}`

/**
 * Leave-policy name for the request picker: drops the trailing entitlement
 * suffix (e.g. "Põhipuhkus (28 päeva)" → "Põhipuhkus"). The day count is the
 * total allowance, not the balance, so it only confuses when choosing a reason.
 * Names without a trailing parenthetical are returned unchanged.
 */
export const policyDisplayName = (name: string): string => name.replace(/\s*\([^)]*\)\s*$/, '')

/** Localized label for an employee status (portal_employees.status). */
export const getStatusLabel = (status: string, lang: Language): string => {
    const labels = t.portal.status
    if (status === 'active') return labels.active(lang)
    if (status === 'on_leave') return labels.onLeave(lang)
    if (status === 'inactive') return labels.inactive(lang)
    return status
}

/** Localized label for a work mode (portal_employment_records.work_mode). */
export const getWorkModeLabel = (workMode: string | null | undefined, lang: Language): string => {
    if (!workMode) return '—'
    const labels = t.portal.profile.workModes
    if (workMode === 'office') return labels.office(lang)
    if (workMode === 'hybrid') return labels.hybrid(lang)
    if (workMode === 'remote') return labels.remote(lang)
    if (workMode === 'abroad') return labels.abroad(lang)
    return workMode
}

/** Format a Date to 'YYYY-MM-DD'. */
export const formatDateStr = (d: Date): string => {
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

/** Today's date as 'YYYY-MM-DD'. */
export const todayStr = (): string => formatDateStr(new Date())

/** BCP 47 locale of a UI language, for the Intl formatters. */
export const localeOf = (lang: Language): string => (lang === 'et' ? 'et-EE' : 'en-GB')

/** Format a UTC timestamp string as a numeric date ('05.06.2026' / '05/06/2026'). */
export const formatLedgerDate = (dateStr: string, lang: Language): string => {
    if (!dateStr) return '—'
    const d = new Date(dateStr.replace(' ', 'T'))
    if (isNaN(d.getTime())) return dateStr
    return d.toLocaleDateString(localeOf(lang), {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    })
}

// Hand-made Estonian month tags: the calendar's day column is ~30 px wide, so the
// ICU short names ("veebr", "märts", "juuni") would overflow; 4 letters fit.
const MONTH_TAGS_ET = [
    'jaan',
    'veeb',
    'märt',
    'apr',
    'mai',
    'juun',
    'juul',
    'aug',
    'sept',
    'okt',
    'nov',
    'dets',
]

/** Short month name of the language, e.g. "Jul" / "juul" (the calendar CSS uppercases it). */
export const formatMonthTag = (date: Date, lang: Language): string =>
    lang === 'et'
        ? MONTH_TAGS_ET[date.getMonth()]
        : new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date)

/** Capitalised weekday name of the language, e.g. "Pühapäev" / "Sunday". */
export const formatWeekday = (date: Date, lang: Language): string => {
    const name = new Intl.DateTimeFormat(localeOf(lang), { weekday: 'long' }).format(date)
    return name.charAt(0).toUpperCase() + name.slice(1)
}

/** Number with the language's digit grouping and decimal separator. */
export const formatNumber = (value: number, lang: Language): string =>
    value.toLocaleString(localeOf(lang))

/** Format a UTC datetime string (e.g. "2026-06-05 13:56:23") as DD.MM.YYYY HH:mm:ss in Estonian local time. */
export const formatUtcTimestamp = (ts: string, lang: Language): string => {
    if (!ts) return '—'
    const iso = ts.replace(' ', 'T')
    // DB strings carry no tz designator; treat them as UTC. Don't double-append if one is present.
    const d = new Date(/[Z]|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + 'Z')
    if (isNaN(d.getTime())) return ts
    return d.toLocaleDateString(lang === 'et' ? 'et-EE' : 'en-GB', {
        timeZone: 'Europe/Tallinn',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })
}

/** Future-relative day phrasing: "today" / "tomorrow" / "in N days". */
export const formatDaysUntil = (daysUntil: number, lang: Language): string => {
    const ph = t.portal.people
    if (daysUntil <= 0) return ph.relToday(lang)
    if (daysUntil === 1) return ph.relTomorrow(lang)
    return ph.relInDays(daysUntil)(lang)
}

/** CSS class for ledger transaction-type badges. */
export const getTypeBadgeClass = (type: string): string => {
    const map: Record<string, string> = {
        ACCRUAL: 'portal-profile__ledger-badge--credit',
        INITIAL_BALANCE: 'portal-profile__ledger-badge--credit',
        DEDUCTION: 'portal-profile__ledger-badge--debit',
        EXPIRATION: 'portal-profile__ledger-badge--expiry',
        MANUAL_ADJUSTMENT: 'portal-profile__ledger-badge--manual',
    }
    return map[type] ?? ''
}

/**
 * Formats an inclusive ISO date range Estonian-style with month names, e.g.
 * "8. – 12. juuni". The year is omitted when start and end share a year;
 * cross-year ranges keep both years. Same-month ranges collapse to a single
 * month suffix; a single day renders as "12. juuni".
 */
export const formatDateRange = (
    startIso: string,
    endIso: string,
    lang: Language = 'et',
): string => {
    // Month name only — strip the leading day and trailing year off the long form.
    const monthName = (iso: string): string =>
        formatDisplayDate(iso, 'long', lang)
            .replace(/^\d+\.?\s*/, '')
            .replace(/\s*\d{4}\s*$/, '')
            .trim()
    const day = (iso: string): string => {
        const d = new Date(iso + 'T00:00:00Z').getUTCDate()
        return lang === 'et' ? `${d}.` : `${d}`
    }

    if (startIso === endIso) return `${day(startIso)} ${monthName(startIso)}`

    const start = new Date(startIso + 'T00:00:00Z')
    const end = new Date(endIso + 'T00:00:00Z')
    const sameYear = start.getUTCFullYear() === end.getUTCFullYear()
    const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth()

    if (sameMonth) {
        // "8. – 12. juuni"
        return `${day(startIso)} – ${day(endIso)} ${monthName(endIso)}`
    }
    if (sameYear) {
        // "28. mai – 3. juuni"
        return `${day(startIso)} ${monthName(startIso)} – ${day(endIso)} ${monthName(endIso)}`
    }
    // Cross-year: keep both years — "28. detsember 2026 – 3. jaanuar 2027".
    return `${formatDisplayDate(startIso, 'long', lang)} – ${formatDisplayDate(endIso, 'long', lang)}`
}

/**
 * Formats an inclusive ISO date range using the compact DD.MM.YYYY form on
 * both sides, e.g. "06.04.2026–10.04.2026". Collapses to a single date when
 * the range is one day. Useful for tight rows (dashboard cards) where the
 * Estonian-style "14.–18. juuli" form is too wide to align cleanly.
 */
export const formatDateRangeShort = (startIso: string, endIso: string): string => {
    if (startIso === endIso) return formatDisplayDate(startIso, 'short')
    return `${formatDisplayDate(startIso, 'short')}–${formatDisplayDate(endIso, 'short')}`
}
